import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useCategories, useSubcategories } from "../../hooks/useCategories";
import {
  cancelButtonStyle,
  primaryButtonStyle,
} from "../../styles/buttonStyles";

interface BulkCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (categoryId: string | null, subcategoryId: string | null) => Promise<void>;
  selectedCount: number;
  isLoading?: boolean;
}

export const BulkCategoryModal: React.FC<BulkCategoryModalProps> = ({
  open,
  onClose,
  onConfirm,
  selectedCount,
  isLoading = false,
}) => {
  const { data: categories } = useCategories();
  const { data: subcategories } = useSubcategories();
  const [categoryId, setCategoryId] = useState<string>("");
  const [subcategoryId, setSubcategoryId] = useState<string>("");

  const handleClose = () => {
    if (!isLoading) {
      setCategoryId("");
      setSubcategoryId("");
      onClose();
    }
  };

  const handleSubmit = async () => {
    await onConfirm(
      categoryId || null,
      subcategoryId || null
    );
    handleClose();
  };

  // Filter subcategories by selected category
  const filteredSubcategories = subcategories?.items?.filter(
    (sub) => !categoryId || sub.category_id === categoryId
  ) || [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, sm: 4 },
          pt: { xs: 3, sm: 4 },
          pb: { xs: 2, sm: 2 },
        }}
      >
        Assign Categories
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 3, sm: 4 },
          py: { xs: 2, sm: 2 },
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Assign categories to {selectedCount} {selectedCount === 1 ? "image" : "images"}
          </Typography>

          <FormControl fullWidth variant="standard">
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId(""); // Reset subcategory when category changes
              }}
              label="Category"
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>None (Clear Category)</em>
              </MenuItem>
              {categories?.items?.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {categoryId && (
            <FormControl fullWidth variant="standard">
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={subcategoryId}
                onChange={(e) => setSubcategoryId(e.target.value)}
                label="Subcategory"
                disabled={isLoading}
              >
                <MenuItem value="">
                  <em>None (Clear Subcategory)</em>
                </MenuItem>
                {filteredSubcategories.map((subcategory) => (
                  <MenuItem key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          gap: { xs: 2, sm: 3 },
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 3 },
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& .MuiButton-root": {
            minHeight: { xs: "52px", sm: "40px" },
            width: { xs: "100%", sm: "auto" },
            border: "none",
            boxShadow: "none",
          },
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isLoading}
          disableRipple
          sx={cancelButtonStyle}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isLoading}
          sx={primaryButtonStyle}
        >
          Apply
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
